import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { CheckOutlined, CloseCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { JobStatus } from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import { Breadcrumb, Button, Dropdown, Input, Layout, MenuProps, Modal, Space, Table } from 'antd';

import { FindJobsByWhereForDetailViewItem } from '@app/auto-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { getSearchFilterDropdown } from '../../../../common';
import { JobNameRenderer, JobStatusRenderer } from '../atoms';
import { jobKeys, useFindJob, useRetryJob } from '../data';
import { JobViewPanel } from '../panels';

const actions: MenuProps['items'] = [
  {
    label: 'Retry',
    key: 'retry',
    icon: <ReloadOutlined />,
  },
  {
    label: 'Mark Resolved',
    key: 'resolved',
    icon: <CheckOutlined />,
    danger: true,
  },
];

export const LandingPage = () => {
  const [jobModalView, setJobModalView] = useState<{ open: boolean; selectedId?: number }>({ open: false });
  const queryClient = useQueryClient();

  // server side table state
  const {
    getCSVLinkProps,
    getParentLayoutContentProps,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getQuery,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<FindJobsByWhereForDetailViewItem>(
    'jobs',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => (
          <Dropdown.Button
            size="small"
            onClick={() => handleJobViewOpen(record.id)}
            menu={{ items: actions, onClick: (e) => handleActionMenuClick(e, record) }}
          >
            <Space>
              <EyeOutlined />
              Details
            </Space>
          </Dropdown.Button>
        ),
      },
      {
        title: 'Id',
        dataIndex: 'id',
        width: 120,
        sorter: true,
        filterDropdown: getSearchFilterDropdown({ placeholder: 'Search Ids', type: 'number' }),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 100,
        sorter: true,
        render: (jobStatus: JobStatus) => <JobStatusRenderer jobStatus={jobStatus} />,
        filters: [
          { text: 'New', value: JobStatus.NEW },
          { text: 'Retry', value: JobStatus.RETRY },
          { text: 'Running', value: JobStatus.RUNNING },
          { text: 'Completed', value: JobStatus.COMPLETED },
          { text: 'Error', value: JobStatus.ERROR },
        ],
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 550,
        render: (name: string) => <JobNameRenderer name={name} showTailNameOnly />,
      },
      {
        title: 'Continuity Id',
        dataIndex: 'continuityId',
        width: 330,
        ellipsis: true,
        responsive: ['xl'],
        sorter: true,
      },
      {
        title: 'Updated By',
        dataIndex: ['updatedByUser', 'name'],
        width: 200,
        ellipsis: true,
        responsive: ['xxl'],
        sorter: true,
      },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        ellipsis: true,
        responsive: ['xxl'],
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      {
        title: 'Created By',
        dataIndex: ['createdByUser', 'name'],
        width: 200,
        ellipsis: true,
        responsive: ['xxl'],
        sorter: true,
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
    ],
    { globalSearchFields: ['name'] },
  );

  const queryResult = useFindJob(
    getQuery({
      where: {
        status: undefined,
      },
    }),
  );
  const retryJobQuery = useRetryJob();

  const handleActionMenuClick = (e: { key: string }, record: FindJobsByWhereForDetailViewItem) => {
    if (e.key === 'retry') {
      retryJobQuery.mutate(record.id, { onSettled: () => queryResult.refetch() });
    } else if (e.key === 'resolved') {
      console.log('add logic to do mark resolved...');
    }
  };

  const handleJobViewOpen = (id: number) => {
    setJobModalView((original) => ({ ...original, open: true, selectedId: id }));
  };

  const handleJobViewClosed = () => {
    setJobModalView((original) => ({ ...original, open: false }));
  };

  const handleJobViewReload = (id: number) => {
    // queryClient.invalidateQueries({ queryKey: jobKeys.list({ jobId: id }) });
    queryClient.invalidateQueries({
      queryKey: jobKeys.list({
        where: { id },
        include: { tasks: true, properties: true, createdByUser: true, updatedByUser: true },
      }),
    });
  };

  return (
    <>
      <Modal
        title="Job"
        centered
        width={1200}
        styles={{
          body: {
            height: 600,
            overflow: 'auto',
          },
        }}
        open={jobModalView.open}
        onCancel={handleJobViewClosed}
        footer={[
          <Button
            key="refresh"
            onClick={() => handleJobViewReload(jobModalView.selectedId || 0)}
            icon={<ReloadOutlined />}
          >
            Refresh
          </Button>,
          <Button key="close" onClick={handleJobViewClosed} icon={<CloseCircleOutlined />} type="primary">
            Close
          </Button>,
        ]}
      >
        {jobModalView.selectedId ? <JobViewPanel id={jobModalView.selectedId} /> : undefined}
      </Modal>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Automation' }, { title: 'Jobs' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Jobs" />

        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
